import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme(); // eslint-disable-line @typescript-eslint/no-unused-vars

  return (
    <div className="app">
      <div className="container" style={{ maxWidth: '800px', padding: '20px' }}>
        {/* Header with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '32px',
            paddingTop: '20px'
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              padding: '8px',
              marginLeft: '-8px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            color: 'var(--text-primary)',
            lineHeight: '1.7'
          }}
        >
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '8px',
            color: 'var(--text-primary)'
          }}>
            Terms of Service
          </h1>
          
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            marginBottom: '32px'
          }}>
            Last Updated: December 30, 2025
          </p>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              1. Acceptance of Terms
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              By accessing and using miiaitor ("the Service"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              2. Description of Service
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              miiaitor is a location-based tour generation platform that creates personalized tours based on your 
              interests and location. The Service uses AI technology to curate experiences tailored to your preferences. 
              Tour generation typically takes 1 to 3 minutes depending on the number of interests selected, with audio 
              narration taking an additional 2 to 3 minutes to generate.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              All tours include research citations with links to sources for facts presented. Tours you create become 
              available to the rest of the community, building a shared library of local knowledge. During tour creation, 
              you may optionally select up to 3 specific places to learn about; if no places are selected, the tour will 
              focus on the street and general area.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              3. User Accounts
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              To access certain features of the Service, you must create an account using Google authentication. 
              You are responsible for:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Maintaining the security of your account</li>
              <li style={{ marginBottom: '8px' }}>All activities that occur under your account</li>
              <li style={{ marginBottom: '8px' }}>Notifying us immediately of any unauthorized use</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              4. Credits and Payments
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              The Service uses a credit-based system:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Credits are used to generate personalized tours</li>
              <li style={{ marginBottom: '8px' }}>Credits can be purchased through our payment provider (Paystack)</li>
              <li style={{ marginBottom: '8px' }}>
                Credit purchases are final and non-refundable, except in cases of technical failure 
                (such as system errors, failed tour generation, or duplicate charges). See our{' '}
                <Link 
                  to="/refund-policy" 
                  style={{ 
                    color: 'var(--primary-color)', 
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}
                >
                  Refund Policy
                </Link>{' '}
                for complete details on eligibility and the refund request process.
              </li>
              <li style={{ marginBottom: '8px' }}>Unused credits purchased individually do not expire</li>
              <li style={{ marginBottom: '8px' }}>Subscription credits refresh monthly and do not roll over to the next billing period</li>
              <li style={{ marginBottom: '8px' }}>All payments are processed securely through Paystack</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              5. User Content and Conduct
            </h2>
            
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              5.1 Community-Shared Tours
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              All tours you generate become part of the community library and are made available to other users. 
              By creating tours, you agree that:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Your generated tours may be viewed, saved, and shared by other users</li>
              <li style={{ marginBottom: '8px' }}>Tours remain accessible to the community even after you delete them from your collection</li>
              <li style={{ marginBottom: '8px' }}>Tour content is generated by AI based on publicly available information and your selected interests</li>
              <li style={{ marginBottom: '8px' }}>You may delete tours you created, which removes them from the community library</li>
            </ul>

            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
              5.2 Conduct Guidelines
            </h3>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              You agree to use the Service in compliance with all applicable laws and regulations. You agree not to:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Use the Service for any illegal purpose</li>
              <li style={{ marginBottom: '8px' }}>Attempt to gain unauthorized access to the Service</li>
              <li style={{ marginBottom: '8px' }}>Interfere with or disrupt the Service</li>
              <li style={{ marginBottom: '8px' }}>Use automated means to access the Service without permission</li>
              <li style={{ marginBottom: '8px' }}>Violate the rights of other users or third parties</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              6. Location Services
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              The Service requires access to your location to provide personalized tour recommendations. 
              By using the Service, you consent to the collection and use of your location data as described 
              in our Privacy Policy. You can control location permissions through your device settings.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              7. Intellectual Property
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              All content, features, and functionality of the Service, including but not limited to text, graphics, 
              logos, and software, are owned by miiaitor or its licensors and are protected by intellectual property laws. 
              You may not copy, modify, distribute, or create derivative works without explicit permission.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              AI-generated tour content is based on publicly available information from various online sources. 
              All factual claims in tours include citations and links to source materials. Tours are made available 
              to the community under our shared knowledge model.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              8. Third-Party Services
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              The Service integrates with third-party services including:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Google Authentication for login</li>
              <li style={{ marginBottom: '8px' }}>Paystack for payment processing</li>
              <li style={{ marginBottom: '8px' }}>Google Maps for location and mapping services</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Your use of these third-party services is subject to their respective terms and policies.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              9. Disclaimer of Warranties
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. 
              We do not guarantee that the Service will be uninterrupted, secure, or error-free. We do not warrant the 
              accuracy or completeness of tour recommendations.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              10. Limitation of Liability
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, miiaitor SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, 
              OR ANY LOSS OF DATA, USE, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              11. Travel Safety
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              While we strive to provide quality tour recommendations, you are responsible for your own safety during tours. 
              We recommend that you:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Verify operating hours and accessibility of locations</li>
              <li style={{ marginBottom: '8px' }}>Be aware of your surroundings at all times</li>
              <li style={{ marginBottom: '8px' }}>Follow local laws and regulations</li>
              <li style={{ marginBottom: '8px' }}>Use your own judgment regarding safety and suitability of locations</li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              12. Modifications to Service
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We reserve the right to modify, suspend, or discontinue the Service at any time without notice. 
              We may also modify these Terms from time to time. Continued use of the Service after changes 
              constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              13. Termination
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We may terminate or suspend your account and access to the Service immediately, without prior notice, 
              for any violation of these Terms. Upon termination, your right to use the Service will cease immediately. 
              Purchased credits will be forfeited upon account termination for Terms violations.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              14. Governing Law
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              These Terms shall be governed by and construed in accordance with applicable laws, without regard to 
              conflict of law provisions. Any disputes arising from these Terms or use of the Service shall be 
              resolved through binding arbitration.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              15. Contact Information
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              If you have any questions about these Terms, please contact us through the app or via our support channels.
            </p>
          </section>

          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              16. Entire Agreement
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              These Terms, together with our{' '}
              <Link 
                to="/privacy-policy" 
                style={{ 
                  color: 'var(--primary-color)', 
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link 
                to="/refund-policy" 
                style={{ 
                  color: 'var(--primary-color)', 
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Refund Policy
              </Link>
              , constitute the entire agreement between you and miiaitor 
              regarding the use of the Service and supersede all prior agreements and understandings.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
